import { APP_INITIALIZER, ApplicationConfig, inject, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
// import { initializeAppCheck, provideAppCheck, ReCaptchaV3Provider } from '@angular/fire/app-check';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';

import { ANNOTATOR_FIREBASE_CONFIG_TOKEN, provideAnnotatorConfig } from '@veamstudios/annotator-sdk';
// import { setLogLevel } from 'firebase/app';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { demoProviders } from './demo/demo.providers';
import { PendingChangesGuard } from './core/guards/pending-changes.guard';
import { RemoteConfigService } from './core/services/remote-config.service';

if (environment.enableAppCheckDebug && isDevMode()) {
  // @ts-expect-error enabled to allow app check to work in dev mode
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

function firebaseAppInitializer() {
  // setLogLevel('debug');
  const app = initializeApp(environment.firebase);
  return app;
}

function remoteConfigInitializer() {
  const remoteConfigService = inject(RemoteConfigService);
  const timeoutMs = 2000;
  return () =>
    Promise.race([
      remoteConfigService.init(),
      new Promise<void>((resolve) => {
        setTimeout(resolve, timeoutMs);
      }),
    ]);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideFirebaseApp(firebaseAppInitializer),
    // TEMPORARILY DISABLED - App Check for testing
    // provideAppCheck(() => {
    //   const result = initializeAppCheck(getApp(), {
    //     provider: new ReCaptchaV3Provider(environment.recaptchaSiteKey),
    //     isTokenAutoRefreshEnabled: true,
    //   });
    //   return result;
    // }),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideDatabase(() => getDatabase()),
    provideFunctions(() => getFunctions(undefined, 'europe-west1')),
    provideMessaging(() => getMessaging()),
    providePerformance(() => getPerformance()),
    provideStorage(() => getStorage()),
    provideRemoteConfig(() => getRemoteConfig()),
    {
      provide: APP_INITIALIZER,
      useFactory: remoteConfigInitializer,
      multi: true,
    },
    provideAnimationsAsync(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.pwa && !environment.demoMode,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    PendingChangesGuard,

    {
      provide: ANNOTATOR_FIREBASE_CONFIG_TOKEN,
      useValue: environment.firebase,
    },
    ...provideAnnotatorConfig({
      provider: 'firebase',
      featureFlags: {
        debugTools: environment.enableAnnotatorDebugTools,
      },
    }),

    // Demo Mode (capture only): composition-root swaps for forged data + fake auth.
    // No-op array unless environment.demoMode is true, so it's stripped from prod/dev/beta.
    ...demoProviders(),
  ],
};
